import { useCallback, useMemo, useState } from 'react';

export type TimeUnit = 'years' | 'months' | 'days' | 'hours' | 'minutes';

export interface TimeCounterValue {
  years: number;
  months: number;
  days: number;
  hours: number;
  minutes: number;
}

const defaultUnits: TimeUnit[] = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
];

export interface UseTimeCounterParams {
  initial?: Partial<TimeCounterValue>;
  min?: Partial<TimeCounterValue>;
  max?: Partial<TimeCounterValue>;
  onChange?: (value: TimeCounterValue) => void;
  label?: string;
  units?: TimeUnit[];
}

const defaults: TimeCounterValue = {
  years: 0,
  months: 0,
  days: 0,
  hours: 0,
  minutes: 0,
};

export const timeBounds = {
  years: { min: 0, max: 99 },
  months: { min: 0, max: 11 },
  days: { min: 0, max: 31 },
  hours: { min: 0, max: 23 },
  minutes: { min: 0, max: 59 },
} as const;

export function useTimeCounter({
  initial,
  min,
  max,
  onChange,
  label,
  units,
}: UseTimeCounterParams = {}) {
  const mergedInitial: TimeCounterValue = { ...defaults, ...(initial ?? {}) };
  const [value, setValue] = useState<TimeCounterValue>(mergedInitial);

  const clamp = useCallback(
    (unit: TimeUnit, v: number) => {
      const lo = min?.[unit] ?? timeBounds[unit].min;
      const hi = max?.[unit] ?? timeBounds[unit].max;
      return Math.max(lo, Math.min(hi, v));
    },
    [min, max]
  );

  const setUnit = useCallback(
    (unit: TimeUnit, v: number) => {
      setValue(prev => {
        const next = { ...prev, [unit]: clamp(unit, v) } as TimeCounterValue;
        onChange?.(next);
        return next;
      });
    },
    [clamp, onChange]
  );

  const inc = useCallback(
    (unit: TimeUnit) => setUnit(unit, value[unit] + 1),
    [setUnit, value]
  );
  const dec = useCallback(
    (unit: TimeUnit) => setUnit(unit, value[unit] - 1),
    [setUnit, value]
  );

  const columns = useMemo(
    () =>
      (units && units.length ? units : defaultUnits).map(unit => ({ unit })),
    [units]
  );

  return { value, setUnit, inc, dec, columns };
}
